import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { X, Copy, Share2, Check, Lock, ArrowRight, Sparkles, GraduationCap, Users, PartyPopper, PlusCircle } from 'lucide-react';
import { db, auth, analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { useIdentity } from '../context/IdentityContext';

// Generate a short room code like "VIBE-7X3K"
const generateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no I,O,0,1 to avoid confusion
    let code = '';
    for (let i = 0; i < 8; i++) {
        code += chars[Math.floor(Math.random() * chars.length)];
        if (i === 3) code += '-';
    }
    return code;
};

const randomNames = [
    'Mystic', 'Shadow', 'Echo', 'Blaze', 'Luna', 'Nova', 'Sky', 'Zephyr', 'Raven',
    'Vortex', 'Nebula', 'Drift', 'Vibe', 'Frost', 'Phantom', 'Wraith', 'Dusk',
    'Haze', 'Storm', 'Rogue', 'Pulse', 'Ember', 'Flicker', 'Shade', 'Bolt', 'Glimmer',
    'Surge', 'Flare', 'Cosmo', 'Daze', 'Sable', 'Nyx', 'Rift', 'Glow', 'Specter',
    'Chill', 'Havoc', 'Mist', 'Stryke', 'Zest', 'Dawn', 'Volt', 'Crimson', 'Slate',
    'Kaze', 'Fury', 'Vapor', 'Glint', 'Rebel', 'Ash', 'Tide', 'Zenith', 'Dagger',
    'Star', 'Nerve', 'Blitz', 'Shadez', 'Flux', 'Neon', 'Chaos', 'Verve', 'Rush',
    'Grit', 'Faze', 'Drip', 'Hex', 'Jolt', 'Wisp', 'Scorch', 'Vex', 'Hype', 'Frosty',
    'Slash', 'Glaze', 'Sparkz', 'Eclipse', 'Raze', 'Sway', 'Phantomz', 'Buzz', 'Quartz',
    'Vivid', 'RUSH', 'Craze', 'Driftz', 'Ignite', 'Zest', 'Frenzy', 'Spectral', 'BlazeX',
    'Vaporz', 'Sting', 'Aurora', 'Kryo'
];
const getRandomAlias = () => randomNames[Math.floor(Math.random() * randomNames.length)];
const randomEmojis = ['🔥', '👻', '💀', '👽', '👾', '✨', '🌊', '🌙'];

export const CreateRoomPage = () => {
    const { identityMode } = useIdentity();
    const navigate = useNavigate();
    const [roomName, setRoomName] = useState('');
    const [password, setPassword] = useState('');
    const [hasPassword, setHasPassword] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [createdRoom, setCreatedRoom] = useState(null); // { id, code, name }
    const [copied, setCopied] = useState(false);
    const [copiedCode, setCopiedCode] = useState(false);
    const [showCollegeNames, setShowCollegeNames] = useState(false);

    const user = auth.currentUser;

    useEffect(() => {
        if (!user) navigate('/login');
    }, [user, navigate]);

    const handleCreate = async () => {
        if (isCreating) return;
        setIsCreating(true);

        try {
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            const uData = userDoc.data() || {};
            const myAlias = getRandomAlias();
            const code = generateCode();
            const name = roomName.trim() || `${myAlias}'s Room`;

            const roomData = {
                name,
                code,
                password: hasPassword && password.trim() ? password.trim() : null,
                createdBy: user.uid,
                participantUids: [user.uid],
                participantInfo: {
                    [user.uid]: {
                        alias: myAlias,
                        college: uData.college || 'Unknown',
                        voiceEffect: null,
                        muted: false
                    }
                },
                maxSize: 8,
                isOpen: true,
                showCollegeNames,
                createdAt: serverTimestamp(),
            };

            const roomRef = await addDoc(collection(db, 'customVoiceRooms'), roomData);
            setCreatedRoom({ id: roomRef.id, code, name });
            
            logEvent(analytics, 'custom_room_created', { 
                has_password: hasPassword && password.trim() !== ''
            });
        } catch (e) {
            // console.error("Create room error:", e);
            toast.error("Failed to create room. Please try again.");
            setIsCreating(false);
        }
    };

    const getRoomLink = () => `${window.location.origin}/room/${createdRoom?.code}`;

    const copyLink = () => {
        navigator.clipboard.writeText(getRoomLink());
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const copyCode = () => {
        navigator.clipboard.writeText(createdRoom?.code);
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
    };

    const shareRoom = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: createdRoom?.name,
                    text: `Join my anonymous voice room "${createdRoom?.name}"! Code: ${createdRoom?.code}`,
                    url: getRoomLink()
                });
            } catch (e) {}
        } else {
            copyLink();
        }
    };

    const enterRoom = () => {
        navigate(`/custom-voice-room/${createdRoom.id}`, {
            replace: true,
            state: { roomCode: createdRoom.code }
        });
    };

    if (identityMode === 'real') {
       return (
         <main className="w-full sm:w-[700px] min-h-screen pb-20 sm:pb-0 border-r border-gray-800 flex flex-col items-center justify-center p-8 shrink-0 relative bg-[#0a0a0a]">
            <div className="w-16 h-16 rounded-full bg-[#0BD9E5]/20 text-[#0BD9E5] flex items-center justify-center mb-6">
               <PlusCircle size={32} />
            </div>
            <h2 className="text-[31px] font-extrabold mb-2 leading-tight text-center text-white">Create Room</h2>
            <p className="text-gray-500 text-[15px] text-center">Coming soon for Real Identity mode.</p>
         </main>
       );
    }

    // ========== SHARE SCREEN ==========
    if (createdRoom) {
        return (
            <main className="w-full sm:w-[700px] min-h-screen pb-20 sm:pb-0 border-r border-gray-800 flex flex-col shrink-0 relative overflow-hidden bg-black text-white font-poppins selection:bg-[#0BD9E5]/30">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(255,102,0,0.15),transparent_70%)]" />
                </div>

                <div className="relative z-10 flex flex-col h-full p-6">
                    <div className="flex items-center justify-between mb-8 mt-4">
                        <div className="w-10"></div>
                        <h1 className="text-xl font-bold flex items-center gap-2">
                            <PartyPopper size={18} className="text-[#FF6600]" /> Room Created!
                        </h1>
                        <div className="w-10"></div>
                    </div>

                    <div className="flex-1 flex flex-col items-center justify-center -mt-10">
                        {/* Room Name */}
                        <h2 className="text-3xl font-black mb-8 text-center">{createdRoom.name}</h2>

                        {/* Room Code Card */}
                        <div className="w-full max-w-sm bg-gray-900 border border-gray-800 rounded-3xl p-6 mb-6">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-3">Room Code</p>
                            <div className="flex items-center justify-between">
                                <p className="text-4xl font-black tracking-[0.15em] text-[#FF6600]">{createdRoom.code}</p>
                                <button onClick={copyCode} className="p-3 rounded-xl bg-gray-800 hover:bg-gray-700 transition-colors">
                                    {copiedCode ? <Check size={20} className="text-green-400" /> : <Copy size={20} className="text-gray-400" />}
                                </button>
                            </div>
                        </div>

                        {/* Share Link Card */}
                        <div className="w-full max-w-sm bg-gray-900 border border-gray-800 rounded-3xl p-6 mb-6">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-3">Share Link</p>
                            <p className="text-sm text-gray-400 mb-4 truncate">{getRoomLink()}</p>
                            <div className="flex gap-3">
                                <button onClick={copyLink} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-800 hover:bg-gray-700 transition-colors text-sm font-bold">
                                    {copied ? <><Check size={16} className="text-green-400" /> Copied!</> : <><Copy size={16} /> Copy Link</>}
                                </button>
                                <button onClick={shareRoom} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#FF6600] hover:bg-[#FF6600]/80 transition-colors text-sm font-bold">
                                    <Share2 size={16} /> Share
                                </button>
                            </div>
                        </div>

                        {hasPassword && (
                            <div className="w-full max-w-sm flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/30 mb-6">
                                <Lock size={14} className="text-yellow-500" />
                                <p className="text-xs text-yellow-500">Password protected — share password separately</p>
                            </div>
                        )}
                    </div>

                    {/* Enter Room Button */}
                    <div className="mt-auto pt-6 flex justify-center w-full">
                        <button 
                            onClick={enterRoom}
                            className="w-full max-w-sm h-16 bg-white text-black font-bold uppercase tracking-widest rounded-xl hover:bg-gray-200 transition-colors shadow-[0_0_20px_rgba(255,255,255,0.2)] flex items-center justify-center gap-3"
                        >
                            Enter Room <ArrowRight size={20} />
                        </button>
                    </div>
                </div>
            </main>
        );
    }

    // ========== CREATE FORM ==========
    return (
        <main className="w-full sm:w-[700px] min-h-screen pb-20 sm:pb-0 border-r border-gray-800 flex flex-col shrink-0 relative overflow-hidden bg-black text-white font-poppins selection:bg-[#0BD9E5]/30">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,102,0,0.1),transparent_70%)]" />
                <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />
            </div>

            <div className="relative z-10 flex flex-col h-full p-6">
                <div className="flex items-center justify-between mb-12 mt-4">
                    <button onClick={() => navigate('/custom-rooms', { replace: true })} className="text-gray-400 hover:text-white transition-colors p-2 bg-white/5 rounded-full">
                        <X size={24} />
                    </button>
                    <h1 className="text-xl font-bold flex items-center gap-2">
                        <Sparkles size={18} className="text-[#FF6600]" /> Create Room
                    </h1>
                    <div className="w-10"></div>
                </div>

                <div className="flex-1 flex flex-col items-center justify-center -mt-16">

                    <div className="w-full max-w-sm space-y-6">
                        {/* Room Name */}
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2 block">Room Name (optional)</label>
                            <input
                                type="text"
                                value={roomName}
                                onChange={(e) => setRoomName(e.target.value)}
                                placeholder="e.g. Late Night Vibes"
                                maxLength={30}
                                className="w-full bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4 text-white placeholder-gray-600 outline-none focus:border-[#FF6600]/50 transition-colors text-lg"
                            />
                        </div>

                        {/* Password Toggle */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2">
                                    <Lock size={12} /> Password Protection
                                </label>
                                <button 
                                    onClick={() => setHasPassword(!hasPassword)}
                                    className={`w-12 h-6 rounded-full transition-colors relative ${hasPassword ? 'bg-[#FF6600]' : 'bg-gray-700'}`}
                                >
                                    <motion.div 
                                        animate={{ x: hasPassword ? 24 : 2 }}
                                        className="absolute top-1 w-4 h-4 rounded-full bg-white shadow"
                                    />
                                </button>
                            </div>
                            {hasPassword && (
                                <motion.input
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    type="text"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter a password"
                                    maxLength={20}
                                    className="w-full bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4 text-white placeholder-gray-600 outline-none focus:border-[#FF6600]/50 transition-colors"
                                />
                            )}
                        </div>

                        {/* College Name Toggle */}
                        <div className="flex items-center justify-between p-4 rounded-2xl bg-gray-900/50 border border-gray-800">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-[#FF6600]/10 border border-[#FF6600]/20 flex items-center justify-center">
                                    <GraduationCap size={18} className="text-[#FF6600]" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold">Show College Names</p>
                                    <p className="text-xs text-gray-500">Allow users to see which college you're from</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setShowCollegeNames(!showCollegeNames)}
                                className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${showCollegeNames ? 'bg-[#FF6600]' : 'bg-gray-700'}`}
                            >
                                <motion.div 
                                    animate={{ x: showCollegeNames ? 24 : 2 }}
                                    className="absolute top-1 w-4 h-4 rounded-full bg-white shadow"
                                />
                            </button>
                        </div>

                        {/* Info */}
                        <div className="flex items-center gap-3 p-4 rounded-2xl bg-gray-900/50 border border-gray-800">
                            <div className="w-10 h-10 rounded-2xl bg-white/5 border border-gray-800 flex items-center justify-center">
                                <Users size={18} className="text-gray-300" />
                            </div>
                            <div>
                                <p className="text-sm font-bold">Up to 8 people</p>
                                <p className="text-xs text-gray-500">Share the code or link with friends to join</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Create Button */}
                <div className="mt-auto pt-8 flex justify-center w-full">
                    <button 
                        onClick={handleCreate}
                        disabled={isCreating}
                        className="w-full max-w-sm h-16 bg-[#FF6600] text-white font-bold uppercase tracking-widest rounded-xl hover:bg-[#FF6600]/90 transition-colors shadow-[0_0_30px_rgba(255,102,0,0.3)] flex items-center justify-center gap-3 disabled:opacity-50"
                    >
                        {isCreating ? 'Creating...' : 'Create Room'}
                    </button>
                </div>
            </div>
        </main>
    );
};

export default CreateRoomPage;

